"use client";

import { Loader2, MinusIcon, PlusIcon, Trash2Icon } from "lucide-react";

import { Button } from "@fluid-app/ui";

export interface QuantityModifierProps {
  /**
   * Current quantity value
   */
  quantity: number;

  /**
   * Callback when quantity should be incremented
   */
  onIncrement: () => void;

  /**
   * Callback when item should be removed or decremented
   * If quantity is 1, this should remove the item
   */
  onRemove: () => void;

  /**
   * Whether the control is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the control is in a loading state
   * @default false
   */
  isLoading?: boolean;

  /**
   * Optional CSS class name
   */
  className?: string;
}

/**
 * Quantity modifier component with rounded corners and spaced buttons
 */
export function QuantityModifier({
  quantity,
  onIncrement,
  onRemove,
  disabled = false,
  isLoading = false,
  className = "",
}: QuantityModifierProps) {
  // Determine if we should show trash icon (quantity = 1) or minus icon (quantity > 1)
  const isRemoveAction = quantity === 1;

  // Fixed size values
  const iconSize = "h-3 w-3";

  return (
    <div
      className={`flex h-[30px] w-[90px] items-center justify-center space-x-2 border border-primary ${className}`}
      style={{ borderRadius: "16px" }} // This is a temporary fix for the Tailwind border-radius issue
    >
      {isLoading ? (
        // Show only centered loading spinner when loading
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
      ) : (
        // Show normal controls when not loading (can be disabled)
        <>
          <Button
            type="button"
            variant={"ghost"}
            size={"icon"}
            onClick={onRemove}
            disabled={disabled || isLoading}
            aria-label={isRemoveAction ? "Remove item" : "Decrease quantity"}
          >
            {isRemoveAction ? (
              <Trash2Icon className={iconSize} />
            ) : (
              <MinusIcon className={iconSize} strokeWidth={3} />
            )}
          </Button>

          <span className="w-7 text-center text-xs font-semibold text-foreground">
            {quantity}
          </span>

          <Button
            type="button"
            variant={"ghost"}
            size={"icon"}
            onClick={onIncrement}
            disabled={disabled || isLoading}
            aria-label="Increase quantity"
          >
            <PlusIcon className={iconSize} strokeWidth={3} />
          </Button>
        </>
      )}
    </div>
  );
}
